import _defineProperty from "@babel/runtime/helpers/defineProperty";
/*
 *                     NOTICE
 *            COPYRIGHT 2018 Ab Initio
 *       UNPUBLISHED - ALL RIGHTS RESERVED
 *
 * This program is confidential and a trade secret of Ab Initio.
 * The receipt or possession of this program does not convey any
 * rights to reproduce or disclose its contents, or to manufacture,
 * use or sell anything that it may describe, in whole or in part,
 * without the specific written consent of Ab Initio.
 */

import { ComponentIdentity } from "abreact/core/identity/ComponentIdentity";
import { PageStateLogger } from "abreact/core/pagestate/PageStateLogger";
import { PageStateUtils } from "abreact/core/reload/PageStateUtils";
import { ClassUtils } from "abweb/utils/ClassUtils";
import * as React from "react";
import { isValidElement } from "react";

// can't be a symbol. React won't let you use one as a prop name
export const computed_identity = "abInternalComputedIdentity";
export const component_identity = Symbol("identity");
export const identity_internal_field = Symbol("_identity");
/*
Create with a default that will be thrown away to ensure that it isn't typed as possibly undefined. It should
never be.
 */
export const IdentityContext = /*#__PURE__*/React.createContext(undefined);
IdentityContext.displayName = "IdentityContext";
export function container(target) {
  PageStateUtils.logDeprecatedWarningMessage("container");
  return target;
}
export function identityParticipant(target) {
  PageStateUtils.logDeprecatedWarningMessage("identityParticipant");
  return target;
}
export class IdentityUtils {
  static omitFromIdentity(node) {
    const name = IdentityUtils.findReactNodeName(node);
    return name != null && IdentityUtils.omittedComponentNames.has(name);
  }
  static createPlaceHolderParentIdentity(component) {
    const componentIdentity = IdentityUtils.getComponentIdentity(component);
    const parentIdentity = new ComponentIdentity(`${componentIdentity.name}-parent`, `${componentIdentity.name}-parent`);
    return parentIdentity;
  }
  static renderContentWithIdentityForClassComponentInternal(parentComponent, content) {
    if (!content) {
      return null;
    } else {
      const childrenIdentityDuplicateMap = new Map();
      /*
      Passed down to children in case they compute theri own identity instead of using the one
      computed for them. This allows them to disambiguate duplicate qualified ids
      */
      const qualifiedIdDuplicateMap = new Map();
      return React.Children.map(content, child => {
        return /*#__PURE__*/React.createElement(IdentityContext.Consumer, null, emittingContainerIdentityContext => {
          if (emittingContainerIdentityContext) {
            const emittingContainerIdentity = IdentityUtils.establishOrGetComponentIdentity(parentComponent, emittingContainerIdentityContext.componentIdentity);
            return IdentityUtils.renderNodeWithIdentity(emittingContainerIdentity, child, qualifiedIdDuplicateMap, childrenIdentityDuplicateMap);
          } else {
            return child;
          }
        });
      });
    }
  }
  static establishOrGetComponentIdentity(component, parentIdentity) {
    if (!IdentityUtils.isIdentifiable(component)) {
      IdentityUtils.makeComponentIdentifiable(component);
      IdentityUtils.initializeIdentity(component, parentIdentity);
    }
    return IdentityUtils.convertToIdentifiable(component)[component_identity];
  }
  static renderNodeWithIdentity(incomingIdentity, node, qualifiedIdDuplicateMap, childrenIdentityDuplicateMap) {
    if (incomingIdentity) {
      /*
      Passed down to children in case they compute theri own identity instead of using the one
      computed for them. This allows them to disambiguate duplicate qualified ids
      */
      const childIdentity = IdentityUtils.createIdentityForChild(node, childrenIdentityDuplicateMap, incomingIdentity);
      const childToRender = /*#__PURE__*/isValidElement(node)
      // built in types, like div, span, or pre, will have a 'string' type. Classes and functions will be function or object
      // No need to pass a computed identity on to a div
      && !(typeof node.type === 'string') && childIdentity ? /*#__PURE__*/React.cloneElement(node, {
        [computed_identity]: childIdentity
      }) : node;
      return /*#__PURE__*/React.createElement(IdentityContext.Provider, {
        value: {
          componentIdentity: childIdentity ?? incomingIdentity,
          qualifiedIdDuplicateMap: qualifiedIdDuplicateMap
        }
      }, childToRender);
    } else {
      return /*#__PURE__*/React.createElement(React.Fragment, null, node);
    }
  }
  static renderChildrenWithIdentityInternal(incomingIdentity, content) {
    if (!content) {
      return null;
    } else {
      const qualifiedIdDuplicateMap = new Map();
      const childrenIdentityDuplicateMap = new Map();
      return React.Children.map(content, child => {
        return IdentityUtils.renderNodeWithIdentity(incomingIdentity, child, qualifiedIdDuplicateMap, childrenIdentityDuplicateMap);
      });
    }
  }
  static findReactNodeName(node) {
    const childAsAny = node ?? {};
    let getIfString = value => {
      if (typeof value === "string") {
        return value;
      } else if (typeof value === "symbol") {
        const str = value.toString();
        return str.indexOf("react.fragment") === -1 ? str : undefined;
      } else {
        return undefined;
      }
    };
    const name = getIfString(childAsAny?.name) ?? getIfString(childAsAny?.type?.displayName) ?? getIfString(childAsAny?.type?.name) ?? getIfString(childAsAny?.displayName) ?? getIfString(childAsAny?.type);
    return name;
  }
  static createIdentityForChild(childReactNode, childrenIdentityDuplicateMap, parentIdentity) {
    let componentIdentity;
    if (!IdentityUtils.omitFromIdentity(childReactNode)) {
      let name = IdentityUtils.findReactNodeName(childReactNode);
      if (childReactNode) {
        const childAsAny = childReactNode ?? {};
        if (name) {
          const key = childAsAny.key || childAsAny.props?.dataTestId || childAsAny.props?.id;
          let qualifiedId = this.createQualifiedIdFromParts(name, key, parentIdentity);
          const [id, message] = IdentityUtils.addIncrementForQualifiedIdIfNeeded(qualifiedId, childrenIdentityDuplicateMap);
          componentIdentity = new ComponentIdentity(name, id, parentIdentity, message);
          componentIdentity.componentIdentifier = qualifiedId.substring((qualifiedId.lastIndexOf(".") || 0) + 1);
          PageStateLogger.maybeLog("identity", `${name} computed identity (as a child): ${qualifiedId}`);
        }
      }
    }
    return componentIdentity;
  }
  static isIdentifiable(obj) {
    if (!obj) {
      return false;
    }
    const componentIdentity = obj[component_identity];
    return componentIdentity && ClassUtils.isInstanceOf(componentIdentity, ComponentIdentity);
  }
  static getComponentIdentity(obj) {
    return IdentityUtils.convertToIdentifiable(obj)[component_identity];
  }
  static convertToIdentifiable(obj) {
    if (IdentityUtils.isIdentifiable(obj)) {
      return obj;
    } else {
      throw new Error("Cannot convert " + ClassUtils.getClassname(obj) + " to IIdentifiableComponent");
    }
  }
  static getBestAvailableIdentifierForComponent(component) {
    let value = component != null ? ClassUtils.getClassname(component) : "unknown";
    if (component != null && IdentityUtils.isIdentifiable(component)) {
      const identity = IdentityUtils.getComponentIdentity(component);
      value = identity.qualifiedId ?? identity.componentIdentifier ?? value;
    }
    return value;
  }
  static unRegisterFromParent(component) {
    if (this.isIdentifiable(component) && component[component_identity].parent) {
      component[component_identity].parent.sequencer.remove(component);
    }
  }
  static createFunctionalComponentIdentity(componentName, parentIdentity, differentiator, qualifiedIdDuplicatedMap) {
    let qualifiedId = IdentityUtils.createQualifiedIdFromParts(componentName, differentiator, parentIdentity);
    const [id, message] = IdentityUtils.addIncrementForQualifiedIdIfNeeded(qualifiedId, qualifiedIdDuplicatedMap);
    qualifiedId = id;
    return new ComponentIdentity(componentName, qualifiedId, parentIdentity, message);
  }
  static addIncrementForQualifiedIdIfNeeded(qualifiedId, qualifiedIdDuplicatedMap) {
    let resultingQualifiedId = qualifiedId;
    let loggableWarning;
    if (qualifiedIdDuplicatedMap) {
      const currentCountOfQualifiedIds = qualifiedIdDuplicatedMap.get(qualifiedId);
      if (currentCountOfQualifiedIds !== undefined) {
        const increment = currentCountOfQualifiedIds + 1;
        qualifiedIdDuplicatedMap.set(qualifiedId, increment);
        resultingQualifiedId = `${qualifiedId}_i${increment}`;
        loggableWarning = `Ambiguous component identity qualifiedId required disambiguation: ${resultingQualifiedId}`;
      } else {
        qualifiedIdDuplicatedMap.set(qualifiedId, 1);
      }
    }
    return [resultingQualifiedId, loggableWarning];
  }

  /**
   * Ensures that the identity of the component is setup correctly given the parent identity.
   *
   * @param {IIdentifiableComponent} component
   * @param {ComponentIdentity} parentIdentity
   */
  static initializeIdentity(component, parentIdentity) {
    if (component[component_identity] && component[component_identity].name) {
      const componentIdentityElements = IdentityUtils.computeIdentityInitializationElements(component, parentIdentity);
      if (!component[component_identity].parent && componentIdentityElements.parentIdentity) {
        // the computed parent may be different then the parent passed in due it being the computed identity from a renderChildrenWithIdentity
        component[component_identity].parent = componentIdentityElements.parentIdentity;
      }
      component[component_identity].componentIdentifier = componentIdentityElements.componentIdentifier;
      component[component_identity].qualifiedId = componentIdentityElements.qualifiedId;
      component[component_identity].loggableWarning = componentIdentityElements.loggableWarning;
      PageStateLogger.maybeLogFor("identity", component, `qualifiedId assigned ${component[component_identity].qualifiedId}`);
    }
  }
  static computeIdentityInitializationElements(component, parentIdentity) {
    const componentIdentityElements = {
      sequence: 0,
      qualifiedId: "",
      componentIdentifier: ""
    };
    if (component.props[computed_identity]) {
      const identity = component.props[computed_identity];
      PageStateLogger.maybeLogFor("identity", component, `using computed identity: ${identity.qualifiedId}`);
      componentIdentityElements.componentIdentifier = identity.componentIdentifier;
      componentIdentityElements.qualifiedId = identity.qualifiedId;
      componentIdentityElements.key = identity.key;
      componentIdentityElements.sequence = identity.sequencer;
      componentIdentityElements.parentIdentity = parentIdentity.parent || undefined;
      componentIdentityElements.loggableWarning = identity.loggableWarning;
    } else {
      componentIdentityElements.parentIdentity = parentIdentity;
      componentIdentityElements.componentIdentifier = component[component_identity].name;
      componentIdentityElements.key = IdentityUtils.findComponentDifferentiator(component);
      if (componentIdentityElements.key) {
        componentIdentityElements.componentIdentifier += "_" + componentIdentityElements.key;
      }
      componentIdentityElements.sequence = parentIdentity ? parentIdentity.sequencer.getSequence(componentIdentityElements.componentIdentifier) : 0;
      if (componentIdentityElements.sequence > 0) {
        componentIdentityElements.loggableWarning = `Ambiguous component identifier for "${componentIdentityElements.componentIdentifier}" within ${parentIdentity.qualifiedId}`;
        componentIdentityElements.componentIdentifier += "_" + componentIdentityElements.sequence;
      }
      if (parentIdentity.qualifiedId) {
        componentIdentityElements.qualifiedId += parentIdentity.qualifiedId + "." + componentIdentityElements.componentIdentifier;
      } else {
        componentIdentityElements.qualifiedId = componentIdentityElements.componentIdentifier;
      }
    }
    return componentIdentityElements;
  }
  static createQualifiedIdFromParts(name, key, parentIdentity) {
    let qualifiedId = parentIdentity?.qualifiedId ? parentIdentity?.qualifiedId : parentIdentity?.name ?? "";
    qualifiedId += qualifiedId.length > 0 ? "." : "";
    qualifiedId += name;
    if (key) {
      qualifiedId += "_" + key;
    }
    return qualifiedId;
  }

  /**
   * Get an identifier for a component: the component's key, and if not there, the component's id prop.
   * Note that according to https://reactjs.org/docs/lists-and-keys.html
   *   "Keys serve as a hint to React but they don’t get passed to your components."
   * To work around this, we rely on unpublished members.
   * @param {IIdentifiableComponent} component
   * @returns {string | null}
   */
  static findComponentReactKey(component) {
    let key;
    if (component) {
      let genericComponent = component;
      if (genericComponent._reactInternals && genericComponent._reactInternals.key) {
        // react 17
        key = genericComponent._reactInternals.key;
      } else if (genericComponent._reactInternalFiber && genericComponent._reactInternalFiber.key) {
        // react 16
        key = genericComponent._reactInternalFiber.key;
      }
    }
    key && PageStateLogger.maybeLogFor("identity", component, `found react key value: ${key}`);
    return key;
  }
  static findComponentDifferentiator(component) {
    let differentiator;
    if (component) {
      differentiator = IdentityUtils.findComponentReactKey(component);
      if (!differentiator) {
        differentiator = IdentityUtils.findPropsDifferentiator(component.props);
        differentiator && PageStateLogger.maybeLogFor("identity", component, `found differentiator: ${differentiator}`);
      }
    }
    return differentiator;
  }
  static findPropsDifferentiator(props) {
    return props ? props.dataTestId || props.id : undefined;
  }
  static renderWithIdentity(component, renderContent, identityFinalizedCallBack) {
    return /*#__PURE__*/React.createElement(IdentityContext.Consumer, null, parentIdentityContext => {
      // let parentComponentIdentityToUse: IComponentIdentity = parentIdentityContext?.componentIdentity ?? ComponentIdentity.createPageLevelIdentity()
      // // tolerate executing in a scenario where there is no root context identity (like samplers or unit tests)
      if (parentIdentityContext != null) {
        const identifiableComponent = IdentityUtils.convertToIdentifiable(component);
        if (!identifiableComponent[component_identity].qualifiedId) {
          IdentityUtils.initializeIdentity(identifiableComponent, parentIdentityContext.componentIdentity);
          if (identityFinalizedCallBack) {
            identityFinalizedCallBack(identifiableComponent);
          }
        }
        return /*#__PURE__*/React.createElement(IdentityContext.Provider, {
          value: {
            componentIdentity: identifiableComponent[component_identity]
          }
        }, renderContent());
      } else {
        return renderContent();
      }
    });
  }
  static omitFromIdentityInternal(component) {
    let name;
    if (typeof component === 'object') {
      name = ClassUtils.getClassname(component);
    } else if (typeof component === 'function') {
      name = component.name;
    } else if (typeof component === 'string') {
      name = component;
    }
    if (name) {
      PageStateLogger.maybeLog("identity", `Registered ${name} to be omitted from identity`);
      IdentityUtils.omittedComponentNames.add(name);
    } else {
      PageStateLogger.maybeLog("identity", `Unable to register for omission from identity. Can't find name for ${component}`);
    }
  }
  static identifiable(target) {
    IdentityUtils.makeComponentIdentifiable(target.prototype);
    return target;
  }
  static makeComponentIdentifiable(component) {
    if (!component[component_identity]) {
      Object.defineProperty(component, component_identity, {
        get() {
          if (!this[identity_internal_field]) {
            const identity = new ComponentIdentity(ClassUtils.getClassname(this));
            this[identity_internal_field] = identity;
            PageStateLogger.maybeLogFor("identity", this, `new identity created ${identity.uniqueId}`);
          }
          return this[identity_internal_field];
        }
      });
    }
    return component;
  }
  static useComponentIdentitySupportInternal(props, componentName, keyFunc) {
    let componentIdentitySupport;
    let componentIdentity;
    // can't short circuit getting this, even though we may not need it.
    // The same hooks have to be called on every invocation or react throws an exception

    // Disabling this lint rule since we know this is only used by functional components
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const incomingIdentityContext = React.useContext(IdentityContext);

    // Disabling this lint rule since we know this is only used by functional components
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const contextRef = React.useRef();
    if (contextRef.current != null) {
      componentIdentitySupport = contextRef.current;
    } else {
      if (props[computed_identity]) {
        componentIdentity = props[computed_identity];
        PageStateLogger.maybeLog("identity", `${componentName} using computed identity: ${componentIdentity?.qualifiedId}`);
      } else if (incomingIdentityContext?.componentIdentity) {
        let differentiator = keyFunc != null ? typeof keyFunc === "string" ? keyFunc : keyFunc() : undefined;
        if (!differentiator) {
          differentiator = IdentityUtils.findPropsDifferentiator(props);
        }
        componentIdentity = IdentityUtils.createFunctionalComponentIdentity(componentName, incomingIdentityContext.componentIdentity, differentiator, incomingIdentityContext.qualifiedIdDuplicateMap);
      }
      if (componentIdentity) {
        componentIdentitySupport = {
          identity: componentIdentity
        };
        contextRef.current = componentIdentitySupport;
        PageStateLogger.maybeLog("identity", `${componentName} assigned qualifiedId: ${componentIdentity.qualifiedId}`);
      }
    }
    return componentIdentitySupport ?? {};
  }
}
_defineProperty(IdentityUtils, "omittedComponentNames", new Set());
//# sourceMappingURL=IdentityUtils.js.map
